import React from 'react'
import { View, TouchableOpacity, Image, StyleSheet } from 'react-native'
import { useNavigation, useTheme } from '@react-navigation/native'
import { Text, Button, EventParticipants, Icon } from '@components'
import { Theme } from '@theme'
import dayjs from 'dayjs'
import { formatDuration } from '@utils'
import {
  CalendarEventUpcomingNotificationData,
  KeepContactReminderNotificationData,
  NotificationType,
  ScheduledMessageUpcomingNotificationData,
  type Notification,
} from '../../../hooks/api/queries'
import { useGetProfileById } from '../../../hooks/api/queries/useGetProfileById'
import { defaultImage } from '@assets'
import { useCreationStackIntegration } from '@hooks'
import { useLanguage } from '@context'
import { getNotificationVisual } from './notificationVisuals'

type FormattedNotification = Notification & {
  message: string
  contactId?: string
  contactIds?: string[]
  contactRequestId?: string
  accepted?: boolean
  isRead: boolean
  description?: string
}

interface NotificationItemProps {
  notification: FormattedNotification
  onPress: () => void
  onAccept?: () => void
  onReject?: () => void
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onPress,
  onAccept,
  onReject,
}) => {
  const navigation = useNavigation()
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyle(theme)

  const {
    showScheduledMessageCreation,
    showReminderCreation,
    showKeepContactCreation,
    isCreationStackEnabled,
  } = useCreationStackIntegration()

  // Get contact profile if contactId is available
  const { data: contactProfile } = useGetProfileById(notification.contactId || '')

  const contactName = contactProfile?.fullName || contactProfile?.firstName || ''
  const isPending = notification.type === NotificationType.CONTACT_REQUEST_CREATED
  const isAnswered = notification.type === NotificationType.CONTACT_REQUEST_ANSWERED
  const isAgendaType =
    notification.type === NotificationType.CALENDAR_EVENT_UPCOMING ||
    notification.type === NotificationType.KEEP_CONTACT_REMINDER ||
    notification.type === NotificationType.SCHEDULED_MESSAGE_UPCOMING ||
    notification.type === NotificationType.BIRTHDAY_UPCOMING

  const isEvent = notification.type === NotificationType.CALENDAR_EVENT_UPCOMING
  const visual = getNotificationVisual(notification.type, theme, notification.accepted)

  const handleNavigate = () => {
    const nav = navigation as any
    if (notification.type === NotificationType.BIRTHDAY_UPCOMING) {
      nav.navigate('Profile', {
        screen: 'DetailsGhostProfile',
        params: { profileId: notification.contactId },
      })
    } else if (notification.type === NotificationType.CALENDAR_EVENT_UPCOMING) {
      if (isCreationStackEnabled) {
        showReminderCreation({
          isCreationStack: true,
          eventId: (notification.data as CalendarEventUpcomingNotificationData).calendarEventId,
        })
      } else {
        nav.navigate('Menu', {
          screen: 'Calendar',
          params: {
            screen: 'AddEvent',
            params: {
              eventId: (notification.data as CalendarEventUpcomingNotificationData).calendarEventId,
            },
          },
        })
      }
    } else if (notification.type === NotificationType.SCHEDULED_MESSAGE_UPCOMING) {
      if (isCreationStackEnabled) {
        showScheduledMessageCreation({
          isCreationStack: true,
          messageId: (notification.data as ScheduledMessageUpcomingNotificationData)
            .scheduledMessageId,
        })
      } else {
        nav.navigate('Menu', {
          screen: 'ScheduledMessages',
          params: {
            screen: 'ComposeMessage',
            params: {
              scheduledMessageId: (notification.data as ScheduledMessageUpcomingNotificationData)
                .scheduledMessageId,
              isEdit: true,
            },
          },
        })
      }
    } else if (notification.type === NotificationType.KEEP_CONTACT_REMINDER) {
      if (isCreationStackEnabled) {
        showKeepContactCreation({
          isCreationStack: true,
          keepContactId: (notification.data as KeepContactReminderNotificationData).keepContactId,
        })
      } else {
        nav.navigate('Menu', {
          screen: 'KeepContact',
          params: {
            screen: 'AddKeep',
            params: {
              keepContactId: (notification.data as KeepContactReminderNotificationData)
                .keepContactId,
            },
          },
        })
      }
    }
  }

  // Tapping a card both marks it read (onPress) and routes to its target.
  const handlePress = () => {
    onPress()
    handleNavigate()
  }

  const showAvatar = !isEvent && Boolean(contactProfile)

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={handlePress}
      style={[styles.card, !notification.isRead && styles.cardUnread]}
    >
      <View style={styles.leading}>
        {showAvatar ? (
          <Image
            source={contactProfile?.avatarUrl ? { uri: contactProfile.avatarUrl } : defaultImage}
            style={styles.avatar}
          />
        ) : (
          <View style={[styles.medallion, { backgroundColor: visual.color + '18' }]}>
            <Icon name={visual.icon} size={theme.iconSize.medium} color={visual.color} />
          </View>
        )}
        {showAvatar && (
          <View
            style={[
              styles.typeBadge,
              { backgroundColor: theme.colors.white, borderColor: visual.color },
            ]}
          >
            <Icon name={visual.icon} size={theme.iconSize.extraSmall} color={visual.color} />
          </View>
        )}
      </View>

      <View style={styles.content}>
        <View style={styles.titleRow}>
          {isAgendaType ? (
            <Text type="bodyBold" color={theme.colors.black} style={styles.flexText}>
              {notification.message}
            </Text>
          ) : (
            <Text type="body" color={theme.colors.black} style={styles.flexText}>
              {contactName ? (
                <Text type="bodyBold" color={theme.colors.black}>
                  {contactName}
                </Text>
              ) : null}
              {contactName ? ' ' : ''}
              <Text type="body" color={theme.colors.greyBlue}>
                {notification.message}
              </Text>
            </Text>
          )}
          <Text type="microText" color={theme.colors.greyBlue} style={styles.time}>
            {formatDuration(dayjs().diff(dayjs(notification.createdAt), 'seconds'))}
          </Text>
        </View>

        {isEvent && (
          <View style={styles.participants}>
            <EventParticipants
              participantAccountIds={
                notification.contactId ? [notification.contactId] : notification.contactIds || []
              }
            />
          </View>
        )}

        {notification.description && (
          <View style={styles.dateChip}>
            <Icon name="clock" size={theme.iconSize.extraSmall} color={theme.colors.greyBlue} />
            <Text type="bodyExtraSmallBold" color={theme.colors.greyBlue}>
              {notification.description}
            </Text>
          </View>
        )}

        {isPending && (
          <View style={styles.actionButtons}>
            <Button
              title={t('notifications.accept')}
              onPress={() => onAccept && onAccept()}
              isSmall
              style={styles.acceptButton}
            />
            <Button
              title={t('notifications.reject')}
              onPress={() => onReject && onReject()}
              unfilled
              isSmall
              style={styles.rejectButton}
            />
          </View>
        )}

        {isAnswered && (
          <Text
            type="bodySmallBold"
            color={notification.accepted ? theme.colors.green : theme.colors.error}
            style={styles.statusText}
          >
            {notification.accepted ? t('notifications.accepted') : t('notifications.rejected')}
          </Text>
        )}
      </View>

      {!notification.isRead && <View style={styles.unreadDot} />}
    </TouchableOpacity>
  )
}

const AVATAR_SIZE = 52

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    card: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      padding: theme.spacing.lg,
      marginBottom: theme.spacing.md,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
      borderWidth: 1,
      borderColor: theme.colors.grey10,
      overflow: 'hidden',
    },
    cardUnread: {
      backgroundColor: theme.colors.primary + '0A',
      borderColor: theme.colors.primary + '26',
    },
    leading: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      marginRight: theme.spacing.md,
    },
    avatar: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      backgroundColor: theme.colors.grey10,
    },
    medallion: {
      width: AVATAR_SIZE,
      height: AVATAR_SIZE,
      borderRadius: AVATAR_SIZE / 2,
      alignItems: 'center',
      justifyContent: 'center',
    },
    typeBadge: {
      position: 'absolute',
      right: -2,
      bottom: -2,
      width: 22,
      height: 22,
      borderRadius: 11,
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: 2,
      borderColor: theme.colors.white,
    },
    content: {
      flex: 1,
      gap: theme.spacing.sm,
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    flexText: {
      flex: 1,
      marginRight: theme.spacing.sm,
    },
    time: {
      marginTop: theme.spacing.xxs,
    },
    participants: {
      marginTop: -theme.spacing.xxs,
    },
    dateChip: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: theme.spacing.xs,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: theme.border.small,
      backgroundColor: theme.colors.grey10,
    },
    actionButtons: {
      flexDirection: 'row',
      marginTop: theme.spacing.xxs,
    },
    acceptButton: {
      marginRight: theme.spacing.sm,
      height: 34,
    },
    rejectButton: {
      height: 34,
    },
    statusText: {
      alignSelf: 'flex-start',
    },
    unreadDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: theme.colors.primary,
      marginLeft: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
  })
